import { Box, Button, Container, Header, SpaceBetween, Spinner, StatusIndicator } from '@amzn/awsui-components-react';
import React from 'react';
import { useLearnerCQProgress, CQModule, CQTranscript } from '../../hooks/cloudQuest/useLearnerCQProgress';
import { TelemetryEvents, TELEMETRY_EVENT_TYPES } from '../../common/TelemetryEvents';
import { useTrackUserEvent } from '../../common/telemetry';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getModuleStatus = (moduleId: string, transcript: CQTranscript | null): 'COMPLETED' | 'IN_PROGRESS' | 'NOT_STARTED' => {
    if (!transcript) return 'NOT_STARTED';
    const detail = transcript.moduleDetails.find((d) => d.moduleLevelCatalogItemID === moduleId);
    return detail?.status ?? 'NOT_STARTED';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface CloudQuestTabProps {
    userId?: string;
    cqCatalogItemId?: string;
}

export const CloudQuestTab = ({ userId, cqCatalogItemId }: CloudQuestTabProps) => {
    const { data, loading, error } = useLearnerCQProgress(userId, cqCatalogItemId ?? '');
    const trackUserEvent = useTrackUserEvent();

    const modules: CQModule[] = data?.catalogItemById?.children?.nodes ?? [];
    const transcript: CQTranscript | null = data?.transcriptByUserIdAndCatalogItemId ?? null;
    const courseName = data?.catalogItemById?.name ?? 'Cloud Quest';
    const totalModules = modules.length;
    const completedModules = transcript?.numberOfCompletedCourses ?? 0;
    const cloudQuestUrl = data?.catalogItemById?.trainingDeliveryUrl;

    if (loading) {
        return (
            <Container>
                <Box textAlign='center' padding='l'>
                    <Spinner size='large' />
                    <Box variant='p' color='text-body-secondary'>
                        Loading Cloud Quest assignments…
                    </Box>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container>
                <Box textAlign='center' padding='l'>
                    <StatusIndicator type='error'>Unable to load Cloud Quest assignments. Please try again later.</StatusIndicator>
                </Box>
            </Container>
        );
    }

    if (modules.length === 0) {
        return (
            <Container>
                <Box textAlign='center' padding='l'>
                    <Box variant='p' color='text-body-secondary'>
                        No assignments available
                    </Box>
                </Box>
            </Container>
        );
    }

    return (
        <SpaceBetween size='s'>
            {/* Header outside container — matches About tab pattern */}
            <Header
                actions={
                    cloudQuestUrl && (
                        <Button
                            variant='normal'
                            iconAlign='right'
                            iconName='external'
                            href={cloudQuestUrl}
                            target='_blank'
                            data-testid='open-cloud-quest-button'
                            onClick={() =>
                                trackUserEvent(TELEMETRY_EVENT_TYPES.CLICK, TelemetryEvents.COURSE_DETAIL_OPEN_CLOUD_QUEST_CLICK)
                            }
                        >
                            Open Cloud Quest
                        </Button>
                    )
                }
            >
                {courseName}
            </Header>

            {/* Assignments list inside container */}
            <Container>
                <SpaceBetween size='xs'>
                    <Box variant='h4'>
                        Assignments{' '}
                        <Box variant='span' color='text-body-secondary' display='inline'>
                            ({completedModules} of {totalModules} complete)
                        </Box>
                    </Box>
                    {modules.map((module) => {
                        const status = getModuleStatus(module.id, transcript);
                        return (
                            <div
                                key={module.id}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    padding: '12px 0',
                                    borderTop: '1px solid #e9ebed',
                                }}
                                data-testid={`cq-module-${module.id}`}
                            >
                                <div style={{ flex: 1 }}>
                                    <Box variant='h4'>{module.name}</Box>
                                    <Box variant='p' color='text-body-secondary'>
                                        {module.description}
                                    </Box>
                                </div>
                                <div style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}>
                                    {status === 'COMPLETED' && (
                                        <StatusIndicator type='success' data-testid={`status-${module.id}`}>
                                            Complete
                                        </StatusIndicator>
                                    )}
                                    {status === 'IN_PROGRESS' && (
                                        <StatusIndicator type='in-progress' data-testid={`status-${module.id}`}>
                                            In progress
                                        </StatusIndicator>
                                    )}
                                    {status === 'NOT_STARTED' && (
                                        <StatusIndicator type='pending' data-testid={`status-${module.id}`}>
                                            Not started
                                        </StatusIndicator>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </SpaceBetween>
            </Container>
        </SpaceBetween>
    );
};
